import { useRef } from 'react';

import { OpportunityStatus } from '@ndvr/customer-api';

import { useGlobalAccount } from '@/views/household/household-queries';
import { useOpportunity, useUpdateOpportunity } from '@/views/opportunities/opportunity-queries';
import {
    OpportunityForm,
    OpportunityFormRef,
    toOpportunityFormValues,
} from '@/views/opportunities/widgets/OpportunityForm';
import { Toast } from '@/widgets/Toast';

import { useDockState } from '../dock-state';
import { UpdateOpportunityDockItem as UpdateOpportunityDockItemType } from '../dock-state-types';

export const UpdateOpportunityDockItem = (props: { item: UpdateOpportunityDockItemType }) => {
    const { data: opportunity, isLoading } = useOpportunity(props.item.data.opportunityGUID);
    const { data: prospect } = useGlobalAccount(props.item.data.prospectGUID);
    const { setActiveId, updateDockItem, removeDockItem } = useDockState();
    const formRef = useRef<OpportunityFormRef>(null);
    const updateOpportunityMutation = useUpdateOpportunity();

    if (isLoading || !opportunity) return null;
    if (props.item.data.prospectGUID && !prospect) return null;

    return (
        <div
            onBlur={() => {
                const formValues = formRef.current?.formMethods.getValues();
                if (formValues) {
                    updateDockItem({
                        ...props.item,
                        data: {
                            ...props.item.data,
                            name: formValues?.name || '',
                            description: formValues?.description || '',
                            value: formValues?.value || '',
                            prospectOption: formValues?.prospectOption,
                            statusOption: formValues?.statusOption || {
                                value: OpportunityStatus.IDENTIFIED,
                                label: 'Identified',
                            },
                        },
                    });
                }
            }}
        >
            <OpportunityForm
                key={props.item.id}
                ref={formRef}
                showProspectInput={false}
                prospectGUID={props.item.data.prospectGUID}
                defaultValues={toOpportunityFormValues(opportunity)}
                submitLabel="Update Opportunity"
                onSubmit={async (data) => {
                    if (updateOpportunityMutation.isPending) return;
                    try {
                        await updateOpportunityMutation.mutateAsync({
                            opportunityGUID: props.item.data.opportunityGUID,
                            request: {
                                name: data.name,
                                description: data.description,
                                status: data.status,
                                value: data.value,
                            },
                        });
                        Toast.success(`Opportunity "${(data.name || 'Untitled').slice(0, 20)}" updated successfully`);
                        removeDockItem(props.item.id);
                    } catch (e) {
                        Toast.forError(new Error('Failed to update opportunity'), 'Failed to update opportunity');
                    }
                }}
                onCancel={() => setActiveId(null)}
            />
        </div>
    );
};
